import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Trash2, Mail, Shield, ArrowLeft } from 'lucide-react';

export default function DataDeletionPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 mb-4 hover:opacity-80">
            <Sparkles className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-heading font-bold text-foreground">AI Marketing Engine</h1>
          </button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-red-500" />
              Data Deletion Instructions
            </CardTitle>
            <CardDescription>
              How to delete your data from AI Marketing Engine
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Introduction */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Your data, your control.</strong> We respect your privacy and make it easy 
                to delete your data at any time. This page explains how to remove your information 
                from our platform.
              </p>
            </div>

            {/* What data we store */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Data We Store
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 text-gray-900">Account Data</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Email address</li>
                    <li>• Name</li>
                    <li>• Language preference</li>
                    <li>• Subscription status</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 text-gray-900">Integration Data</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Social media page IDs</li>
                    <li>• Access tokens (encrypted)</li>
                    <li>• Publishing history</li>
                    <li>• Content calendars</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* What we DON'T store */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Data We Do NOT Store</h3>
              <ul className="text-sm text-gray-600 space-y-1 bg-green-50 rounded-lg p-4">
                <li>• Personal profile content from social networks</li>
                <li>• Friends lists or contacts</li>
                <li>• Private messages</li>
                <li>• Photos from personal profiles</li>
                <li>• Payment card numbers (processed by Stripe)</li>
              </ul>
            </section>

            {/* Self-service deletion */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Self-Service Deletion</h3>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">1. Disconnect Social Integrations</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Go to <strong>Dashboard → Integrations</strong> and click "Disconnect" on each 
                    connected platform (Facebook, X, Telegram, etc.). This immediately deletes:
                  </p>
                  <ul className="text-sm text-gray-500 ml-4">
                    <li>• Access tokens</li>
                    <li>• Page/account connections</li>
                    <li>• API credentials</li>
                  </ul>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">2. Delete Your Projects</h4>
                  <p className="text-sm text-gray-600">
                    Go to <strong>Dashboard → Projects</strong> and delete each project. This removes:
                  </p>
                  <ul className="text-sm text-gray-500 ml-4">
                    <li>• Content calendars</li>
                    <li>• Generated content</li>
                    <li>• Publishing history</li>
                  </ul>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">3. Delete Your Account</h4>
                  <p className="text-sm text-gray-600">
                    Go to <strong>Dashboard → Settings → Account</strong> and click "Delete Account" 
                    to permanently remove all your data.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact for full deletion */}
            <section>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Request Full Data Deletion
              </h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 mb-3">
                  For complete data deletion including all backups, email us at:
                </p>
                <a 
                  href="mailto:privacy@ai-marketing-engine.com?subject=Data Deletion Request"
                  className="inline-flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  privacy@ai-marketing-engine.com
                </a>
                <p className="text-xs text-yellow-700 mt-3">
                  Please include your registered email address. We will process your request within 30 days 
                  as required by GDPR and other privacy regulations.
                </p>
              </div>
            </section>

            {/* Facebook-specific */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Facebook Data Deletion</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>For Facebook users:</strong> When you disconnect Facebook from AI Marketing Engine, 
                  we immediately delete:
                </p>
                <ul className="text-sm text-blue-700 ml-4 mb-3">
                  <li>• Your Facebook Page access tokens</li>
                  <li>• Page IDs and names</li>
                  <li>• Any cached Page information</li>
                </ul>
                <p className="text-sm text-blue-800">
                  You can also revoke access from your Facebook settings:<br/>
                  <a href="https://www.facebook.com/settings?tab=business_tools" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="underline hover:text-blue-600">
                    Facebook → Settings → Business Integrations
                  </a>
                </p>
              </div>
            </section>

            {/* Back button */}
            <div className="pt-4 border-t">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Last updated: December 2024</p>
          <div className="mt-2">
            <a href="/privacy-policy" className="hover:underline">Privacy Policy</a>
            {' • '}
            <a href="/terms" className="hover:underline">Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  );
}
